export interface SupportMessageFormAnswer {
  label: string;
  value: string;
  documentId?: string | null;
  fileName?: string | null;
  contentType?: string | null;
}

export interface SupportMessageFormView {
  isForm: boolean;
  formId?: string | null;
  formTitle?: string | null;
  formPublicId?: string | null;
  formResponseId?: string | null;
  submitted: boolean;
  answers: SupportMessageFormAnswer[];
}

export function parseSupportMessageForm(
  contentAttributes?: string | null
): SupportMessageFormView {
  if (!contentAttributes)
  {
    return {
      isForm: false,
      submitted: false,
      answers: [],
    };
  }

  try
  {
    const parsed = JSON.parse(contentAttributes) as Record<string, unknown>;
    const contentType = String(parsed.contentType ?? '').toLowerCase();
    if (contentType !== 'input_form')
    {
      return {
        isForm: false,
        submitted: false,
        answers: [],
      };
    }

    const formResponseId = typeof parsed.formResponseId === 'string' && parsed.formResponseId.trim()
      ? parsed.formResponseId.trim()
      : null;

    return {
      isForm: true,
      formId: typeof parsed.formId === 'string' ? parsed.formId : null,
      formTitle: typeof parsed.formTitle === 'string' ? parsed.formTitle : null,
      formPublicId: typeof parsed.formPublicId === 'string' ? parsed.formPublicId : null,
      formResponseId,
      submitted: Boolean(formResponseId),
      answers: readFormAnswers(parsed.formAnswers),
    };
  }
  catch
  {
    return {
      isForm: false,
      submitted: false,
      answers: [],
    };
  }
}

function readOptionalString(value: unknown): string | null
{
  if (typeof value !== 'string')
  {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed)
  {
    return null;
  }

  return trimmed;
}

function readFormAnswers(value: unknown): SupportMessageFormAnswer[]
{
  if (!Array.isArray(value))
  {
    return [];
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== 'object')
    {
      return [];
    }

    const record = item as Record<string, unknown>;
    const label = readOptionalString(record.label) || readOptionalString(record.Label);
    if (!label)
    {
      return [];
    }

    return [{
      label,
      value: readOptionalString(record.value) || readOptionalString(record.Value) || '—',
      documentId: readOptionalString(record.documentId) || readOptionalString(record.DocumentId),
      fileName: readOptionalString(record.fileName) || readOptionalString(record.FileName),
      contentType: readOptionalString(record.contentType) || readOptionalString(record.ContentType),
    }];
  });
}

export function formAnswerCanPreview(answer: SupportMessageFormAnswer): boolean
{
  const type = (answer.contentType || '').toLowerCase();
  const name = (answer.fileName || answer.value || '').toLowerCase();
  if (type.includes('pdf') || type.startsWith('image/'))
  {
    return true;
  }

  return name.endsWith('.pdf') || /\.(png|jpe?g|gif|webp)$/.test(name);
}
